import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Trash2, Send, Loader2, PenSquare, Inbox, X, Mail, MailOpen, SendHorizonal } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMailDate = (d) => {
  try {
    return new Date(d).toLocaleString("nb-NO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return d || "";
  }
};

const AdminMail = () => {
  const { authHeader } = useAuth();
  const [folders, setFolders] = useState([]);
  const [view, setView] = useState("INBOX"); // INBOX | UNREAD | Sent | Trash
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [compose, setCompose] = useState(null);
  const [error, setError] = useState("");

  const loadFolders = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/mail/folders`, { headers: authHeader() });
      setFolders(r.data);
    } catch {
      /* folder stats are best-effort */
    }
  }, [authHeader]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = view === "UNREAD" ? { folder: "INBOX", unread: 1 } : { folder: view };
      const r = await axios.get(`${API}/admin/mail/messages`, { headers: authHeader(), params });
      setItems(r.data);
    } catch (e) {
      setError(
        e.response?.status === 502
          ? "Kan ikke nå e-postserveren akkurat nå. Prøv igjen om litt."
          : "Kunne ikke hente e-post."
      );
    } finally {
      setLoading(false);
    }
  }, [authHeader, view]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    setSelected(null);
    load();
  }, [load]);

  const refreshAll = () => {
    load();
    loadFolders();
  };

  const currentFolder = view === "UNREAD" ? "INBOX" : view;

  const open = async (uid) => {
    setOpening(true);
    setCompose(null);
    try {
      const r = await axios.get(`${API}/admin/mail/messages/${uid}`, {
        headers: authHeader(),
        params: { folder: currentFolder },
      });
      setSelected(r.data);
      setReply("");
      setItems((prev) => prev.map((m) => (m.uid === uid ? { ...m, seen: true } : m)));
      loadFolders();
    } catch {
      toast.error("Kunne ikke åpne meldingen");
    } finally {
      setOpening(false);
    }
  };

  const toggleSeen = async () => {
    if (!selected) return;
    const newSeen = !items.find((m) => m.uid === selected.uid)?.seen;
    try {
      await axios.post(
        `${API}/admin/mail/messages/${selected.uid}/seen`,
        { folder: currentFolder, seen: newSeen },
        { headers: authHeader() }
      );
      setItems((prev) => prev.map((m) => (m.uid === selected.uid ? { ...m, seen: newSeen } : m)));
      toast.success(newSeen ? "Merket som lest" : "Merket som ulest");
      loadFolders();
    } catch {
      toast.error("Kunne ikke oppdatere");
    }
  };

  const remove = async (uid) => {
    try {
      await axios.delete(`${API}/admin/mail/messages/${uid}`, {
        headers: authHeader(),
        params: { folder: currentFolder },
      });
      setItems((prev) => prev.filter((m) => m.uid !== uid));
      setSelected(null);
      toast.success(currentFolder === "Trash" ? "E-post slettet permanent" : "Flyttet til papirkurv");
      loadFolders();
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  const sendReply = async () => {
    if (!selected || !reply.trim()) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/mail/send`,
        {
          to: selected.from,
          subject: /^re:/i.test(selected.subject || "") ? selected.subject : `Re: ${selected.subject || ""}`,
          body: reply,
          in_reply_to: selected.message_id || null,
        },
        { headers: authHeader() }
      );
      toast.success("Svar sendt");
      setReply("");
      loadFolders();
    } catch {
      toast.error("Kunne ikke sende svaret");
    } finally {
      setSending(false);
    }
  };

  const sendCompose = async () => {
    if (!compose?.to || !compose?.body?.trim()) {
      toast.error("Fyll inn mottaker og melding");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/admin/mail/send`, compose, { headers: authHeader() });
      toast.success("E-post sendt");
      setCompose(null);
      loadFolders();
    } catch {
      toast.error("Kunne ikke sende e-posten");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "mt-1.5 w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition-colors duration-200 focus:border-sky-600 focus:outline-none";

  const inboxFolder = folders.find((f) => f.id === "INBOX");
  const sentFolder = folders.find((f) => f.id === "Sent");
  const trashFolder = folders.find((f) => f.id === "Trash");

  const views = [
    { id: "INBOX", label: "Innboks", icon: Inbox, unread: inboxFolder?.unread, testid: "mail-folder-inbox" },
    { id: "UNREAD", label: "Uleste", icon: Mail, unread: inboxFolder?.unread, testid: "mail-folder-unread" },
    ...(sentFolder ? [{ id: "Sent", label: "Sendte", icon: SendHorizonal, total: sentFolder.total, testid: "mail-folder-sent" }] : []),
    ...(trashFolder ? [{ id: "Trash", label: "Papirkurv", icon: Trash2, total: trashFolder.total, testid: "mail-folder-trash" }] : []),
  ];

  return (
    <div data-testid="admin-mail-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">E-post · post@norgesconsult.no</h2>
          <p className="mt-0.5 text-sm text-slate-500">Les og svar på e-post direkte fra nettsiden</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setCompose({ to: "", subject: "", body: "" }); setSelected(null); }}
            data-testid="mail-compose-toggle"
            className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-700"
          >
            <PenSquare size={15} />
            Ny e-post
          </button>
          <button
            onClick={refreshAll}
            data-testid="mail-refresh"
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-sky-600 hover:text-sky-700"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            Oppdater
          </button>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="mail-folders">
        {views.map((v) => {
          const Icon = v.icon;
          const active = view === v.id;
          return (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              data-testid={v.testid}
              className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                active
                  ? "border-sky-600 bg-sky-50 text-sky-800"
                  : "border-slate-300 bg-white text-slate-600 hover:border-sky-600 hover:text-sky-700"
              }`}
            >
              <Icon size={15} />
              {v.label}
              {v.unread > 0 && (
                <span className="rounded-full bg-sky-600 px-2 py-0.5 text-xs font-semibold text-white" data-testid={`mail-unread-count-${v.id.toLowerCase()}`}>
                  {v.unread}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-5">
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white lg:col-span-2" data-testid="mail-list">
          {loading ? (
            <div className="flex justify-center p-10"><Loader2 className="animate-spin text-sky-600" size={26} /></div>
          ) : error ? (
            <div className="p-8 text-center">
              <p className="text-sm text-slate-600" data-testid="mail-error">{error}</p>
              <button onClick={refreshAll} className="mt-3 text-sm font-semibold text-sky-700 hover:underline" data-testid="mail-retry">
                Prøv igjen
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center" data-testid="mail-empty">
              <Inbox size={30} className="mx-auto text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">
                {view === "UNREAD" ? "Ingen uleste e-poster." : "Mappen er tom."}
              </p>
            </div>
          ) : (
            <ul className="max-h-[560px] divide-y divide-slate-100 overflow-y-auto">
              {items.map((m) => (
                <li key={m.uid}>
                  <button
                    onClick={() => open(m.uid)}
                    data-testid={`mail-item-${m.uid}`}
                    className={`block w-full px-5 py-4 text-left transition-colors hover:bg-sky-50/60 ${
                      selected?.uid === m.uid ? "border-l-2 border-sky-600 bg-sky-50" : "border-l-2 border-transparent"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {!m.seen && <span className="h-2 w-2 shrink-0 rounded-full bg-sky-600" data-testid={`mail-unread-dot-${m.uid}`} />}
                      <p className={`truncate text-sm text-slate-900 ${m.seen ? "font-normal" : "font-bold"}`}>
                        {m.subject || "(uten emne)"}
                      </p>
                    </div>
                    <p className={`mt-0.5 truncate text-xs ${m.seen ? "text-slate-500" : "font-semibold text-slate-700"}`}>{m.from}</p>
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">{fmtMailDate(m.date)}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 lg:col-span-3" data-testid="mail-read-pane">
          {compose ? (
            <div>
              <div className="flex items-center justify-between">
                <h3 className="font-display text-base font-bold text-slate-900">Ny e-post</h3>
                <button onClick={() => setCompose(null)} className="text-slate-400 hover:text-slate-700" data-testid="mail-compose-cancel" aria-label="Lukk">
                  <X size={18} />
                </button>
              </div>
              <div className="mt-4 space-y-4">
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Til *</label>
                  <input value={compose.to} onChange={(e) => setCompose({ ...compose, to: e.target.value })} data-testid="mail-compose-to" className={inputCls} placeholder="mottaker@firma.no" />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Emne</label>
                  <input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} data-testid="mail-compose-subject" className={inputCls} placeholder="Emne" />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Melding *</label>
                  <textarea rows={8} value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} data-testid="mail-compose-body" className={`${inputCls} resize-y`} placeholder="Skriv meldingen ..." />
                </div>
                <button
                  onClick={sendCompose}
                  disabled={sending}
                  data-testid="mail-compose-send"
                  className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-60"
                >
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  Send e-post
                </button>
              </div>
            </div>
          ) : opening ? (
            <div className="flex justify-center p-10"><Loader2 className="animate-spin text-sky-600" size={26} /></div>
          ) : selected ? (
            <div>
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
                <div>
                  <h3 className="font-display text-base font-bold text-slate-900" data-testid="mail-read-subject">{selected.subject || "(uten emne)"}</h3>
                  <p className="mt-1 text-xs text-slate-500">Fra: {selected.from}</p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{fmtMailDate(selected.date)}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={toggleSeen}
                    data-testid="mail-toggle-seen"
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:border-sky-400 hover:text-sky-700"
                  >
                    <MailOpen size={13} />
                    Merk som ulest
                  </button>
                  <button
                    onClick={() => remove(selected.uid)}
                    data-testid="mail-delete"
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:border-red-300 hover:text-red-600"
                  >
                    <Trash2 size={13} />
                    Slett
                  </button>
                </div>
              </div>
              <pre className="mt-4 max-h-72 overflow-y-auto whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-700" data-testid="mail-read-body">
                {selected.body || "(ingen tekstinnhold)"}
              </pre>
              <div className="mt-5 border-t border-slate-100 pt-5">
                <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Svar</label>
                <textarea
                  rows={4}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  data-testid="mail-reply-input"
                  className={`${inputCls} resize-y`}
                  placeholder="Skriv svar til avsender ..."
                />
                <button
                  onClick={sendReply}
                  disabled={sending || !reply.trim()}
                  data-testid="mail-reply-send"
                  className="mt-3 inline-flex items-center gap-2 rounded-md bg-sky-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-60"
                >
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  Send svar
                </button>
              </div>
            </div>
          ) : (
            <div className="flex h-full min-h-64 flex-col items-center justify-center text-center">
              <Inbox size={30} className="text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">Velg en e-post til venstre for å lese den.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMail;
